// components/SendEmailModal.jsx
import React, { useState, useEffect } from "react";
import { X, Send, FileText } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const SendEmailModal = ({ lead, recipient, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const emailData = recipient || lead;

  const [formData, setFormData] = useState({
    to: emailData?.email || "",
    subject: "",
    body: "",
    bodyHtml: "",
    cc: "",
    bcc: "",
    trackOpens: true,
    trackLinks: true,
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadTemplates();
    checkEmailConnection();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await apiService.getMe();
      if (response.success) setCurrentUser(response.user);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const checkEmailConnection = async () => {
    try {
      const response = await apiService.getEmailConnectionStatus();
      if (!response.data.connected) {
        setEmailStatus({
          connected: false,
          message:
            "Email not connected. Please connect your email in settings.",
        });
      } else {
        setEmailStatus({
          connected: true,
          email: response.data.email,
          fromAddress: "diaryteam@fibretelecomsuk.co.uk",
        });
      }
    } catch (error) {
      console.error("Error checking email connection:", error);
      setEmailStatus({
        connected: false,
        message: "Unable to verify email connection",
      });
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await apiService.getEmailTemplates({
        isActive: true,
        type: "sales_engagement",
      });
      setTemplates(response.data.rows || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "body" ? { bodyHtml: "" } : {}),
    }));
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate("");
      return;
    }
    setSelectedTemplate(templateId);
    try {
      const response = await apiService.getCompanyEmailTemplate(templateId);
      const template = response.data;

      let subject = template.subject;
      let body = template.bodyHtml || template.body;

      const variables = {
        firstName:
          emailData?.lead?.firstName ||
          emailData?.firstName ||
          emailData?.fullName?.split(" ")[0] ||
          "",
        lastName:
          emailData?.lead?.lastName ||
          emailData?.lastName ||
          emailData?.fullName?.split(" ").slice(1).join(" ") ||
          "",
        fullName: (() => {
          if (emailData?.lead?.fullName) return emailData.lead.fullName;
          if (emailData?.lead?.firstName && emailData?.lead?.lastName)
            return `${emailData.lead.firstName} ${emailData.lead.lastName}`;
          if (emailData?.lead?.firstName) return emailData.lead.firstName;
          if (emailData?.fullName) return emailData.fullName;
          if (emailData?.firstName && emailData?.lastName)
            return `${emailData.firstName} ${emailData.lastName}`;
          if (emailData?.firstName) return emailData.firstName;
          return "";
        })(),
        email: emailData?.lead?.email || emailData?.email || "",
        companyName:
          emailData?.lead?.companyName ||
          emailData?.opportunity?.accountName ||
          emailData?.account?.name ||
          emailData?.companyName ||
          "Fibre Telecoms UK",
        title:
          emailData?.lead?.jobTitle ||
          emailData?.title ||
          emailData?.jobTitle ||
          "",
        phone: emailData?.lead?.phone || emailData?.phone || "",
        appointmentTitle: emailData?.appointmentData?.title || "",
        appointmentDate: emailData?.appointmentData?.date || "",
        appointmentTime: emailData?.appointmentData?.time || "",
        appointmentLocation: emailData?.appointmentData?.location || "",
        appointmentDescription: emailData?.appointmentData?.description || "",
        assignedTo: emailData?.appointmentData?.assignedTo || "",
        telesalesPerson:
          emailData?.opportunity?.telesalesPerson ||
          emailData?.appointmentData?.assignedTo ||
          "",
        senderName: currentUser?.name || "",
        senderEmail: "diaryteam@fibretelecomsuk.co.uk",
        companyWebsite:
          emailData?.lead?.companyWebsite ||
          emailData?.companyWebsite ||
          "https://www.fibretelecomsuk.co.uk",
      };

      Object.keys(variables).forEach((key) => {
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
        subject = subject.replace(regex, variables[key]);
        body = body.replace(regex, variables[key]);
      });

      const hasHtmlTags = /<[^>]+>/.test(body);
      const finePrint = `
  <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;" />
  <p style="font-size: 11px; color: #999; font-style: italic; line-height: 1.5;">
    Please be advised that meetings may occasionally be recorded for training purposes and to maintain full transparency between all parties.
  </p>`;

      let htmlBody = body;
      if (hasHtmlTags) {
        htmlBody = htmlBody.includes("</body>")
          ? htmlBody.replace("</body>", `${finePrint}\n</body>`)
          : htmlBody + finePrint;
      } else {
        htmlBody = body
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .map((line) => `<p>${line}</p>`)
          .join("\n");
        htmlBody = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  ${htmlBody}
  ${finePrint}
</body>
</html>`;
      }

      setFormData({ ...formData, subject, body, bodyHtml: htmlBody });
    } catch (error) {
      console.error("Error loading template:", error);
      alert("Failed to load template");
    }
  };

  // ── Smart body parser ────────────────────────────────────────────────
  // Reads salutation, body paragraphs, closing phrase and sender name
  // directly from whatever is in the textarea — including after manual edits.
  const parseBodyForPrint = (rawBody) => {
    const htmlEntityMap = {
      "&amp;": "&",
      "&lt;": "<",
      "&gt;": ">",
      "&nbsp;": " ",
      "&#39;": "'",
      "&quot;": '"',
    };

    const plainText = rawBody
      .replace(/<\/(p|div|li|tr|h[1-6])[^>]*>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&[a-z#0-9]+;/gi, (m) => htmlEntityMap[m] ?? "")
      .replace(/[ \t]+/g, " ")
      .trim();

    const allLines = plainText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    // Extract "Dear ..." salutation from first line
    let salutation = null;
    let contentStartIdx = 0;
    if (allLines.length > 0 && /^dear\b/i.test(allLines[0])) {
      salutation = allLines[0].replace(/[,\s]+$/, "") + ",";
      contentStartIdx = 1;
    }

    // Walk backwards to find where the sign-off block starts
    const signOffRe =
      /^(best regards|many thanks|kind regards|yours sincerely|yours faithfully|regards|thank you|thanks|warm regards|with kind regards)/i;
    const knownSenderRe =
      /^(mofeo|fibre telecoms( uk)?|account manager|junior account manager|sam remfry|rhys smith|chris mcgory|diary team|the appointments team)/i;

    let signOffIdx = allLines.length;
    for (let i = allLines.length - 1; i >= contentStartIdx; i--) {
      if (signOffRe.test(allLines[i]) || knownSenderRe.test(allLines[i])) {
        signOffIdx = i;
      } else {
        break;
      }
    }

    const signOffBlock = allLines.slice(signOffIdx);
    const closingLine = signOffBlock.find((l) => signOffRe.test(l));
    const closing = closingLine
      ? closingLine.replace(/[,\s]+$/, "") + ","
      : "Kind regards,";

    const senderLineFromBody = signOffBlock.find(
      (l) => !signOffRe.test(l) && l.length > 0,
    );

    return {
      salutation,
      body: allLines.slice(contentStartIdx, signOffIdx).join("\n\n"),
      closing,
      senderName: currentUser?.name || senderLineFromBody || "Diary Team",
    };
  };

  // ── Smart address parser ─────────────────────────────────────────────
  // Returns an array of address lines ready to render as separate <p> tags.
  // Handles three scenarios:
  //   1. Discrete fields available (address + city + postcode)
  //   2. Single combined string e.g. "71 South Rd, Haywards Heath RH16 4LQ, UK"
  //   3. Only an address field with no commas
  const parseAddressForPrint = (address, city, postcode) => {
    // UK postcode pattern
    const ukPostcodeRe = /\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/i;
    // Country-only patterns to strip from the end
    const countryRe = /^(uk|united kingdom|england|wales|scotland)$/i;

    // Case 1: we already have clean separate fields — just use them
    if (city || postcode) {
      return [address, city, postcode].filter(Boolean);
    }

    // Case 2: single combined string — split and tidy up
    if (address && address.includes(",")) {
      const parts = address
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);

      // Try to separate an inline postcode from its city part
      // e.g. "Haywards Heath RH16 4LQ" → ["Haywards Heath", "RH16 4LQ"]
      const expanded = [];
      parts.forEach((part) => {
        const postcodeMatch = part.match(ukPostcodeRe);
        if (postcodeMatch) {
          const postcodePart = postcodeMatch[0].trim();
          const cityPart = part.replace(ukPostcodeRe, "").trim();
          if (cityPart) expanded.push(cityPart);
          expanded.push(postcodePart);
        } else {
          expanded.push(part);
        }
      });

      // Drop trailing country line — the letterhead already shows UK
      const withoutCountry = expanded.filter((p) => !countryRe.test(p.trim()));
      return withoutCountry.length > 0 ? withoutCountry : expanded;
    }

    // Case 3: single line, no commas — return as-is
    return [address].filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.to || !formData.subject || !formData.body) {
      alert("Please fill in all required fields");
      return;
    }
    if (!emailStatus?.connected) {
      alert("Email is not connected. Please connect your email in settings.");
      return;
    }

    setLoading(true);

    try {
      const finePrintHtml = `
  <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;" />
  <p style="font-size: 11px; color: #999; font-style: italic; line-height: 1.5;">
    Please be advised that meetings may occasionally be recorded for training purposes and to maintain full transparency between all parties.
  </p>`;

      const rawHtml =
        formData.bodyHtml ||
        `<p>${formData.body.replace(/\n/g, "</p><p>")}</p>`;
      const finalBodyHtml = formData.bodyHtml
        ? rawHtml
        : rawHtml + finePrintHtml;

      const emailPayload = {
        to: formData.to,
        subject: formData.subject,
        body: formData.body,
        bodyHtml: finalBodyHtml,
        cc: formData.cc ? formData.cc.split(",").map((e) => e.trim()) : [],
        bcc: formData.bcc ? formData.bcc.split(",").map((e) => e.trim()) : [],
        from: "diaryteam@fibretelecomsuk.co.uk",
        fromName: "Fibre Telecoms UK",
        leadId: emailData?.leadId || lead?.id,
        opportunityId: emailData?.opportunityId,
        appointmentId: emailData?.appointmentId,
        accountId: emailData?.accountId,
        trackOpens: formData.trackOpens,
        trackLinks: formData.trackLinks,
      };

      await apiService.sendEmail(emailPayload);

      // ── Date ────────────────────────────────────────────────────────
      const now = new Date();
      const day = now.getDate();
      const ordinal = (d) => {
        const s = ["th", "st", "nd", "rd"];
        const v = d % 100;
        return d + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const dateWithOrdinal = now
        .toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        .replace(/^\d+/, ordinal(day));

      // ── Recipient name ───────────────────────────────────────────────
      const recipientFirstName =
        emailData?.lead?.firstName ||
        emailData?.firstName ||
        emailData?.fullName?.split(" ")[0] ||
        "";
      const recipientLastName =
        emailData?.lead?.lastName ||
        emailData?.lastName ||
        emailData?.fullName?.split(" ").slice(1).join(" ") ||
        "";
      const recipientName =
        emailData?.lead?.fullName ||
        (recipientFirstName && recipientLastName
          ? `${recipientFirstName} ${recipientLastName}`
          : recipientFirstName) ||
        emailData?.fullName ||
        "Valued Customer";
      const recipientCompany =
        emailData?.lead?.companyName ||
        emailData?.opportunity?.accountName ||
        emailData?.account?.name ||
        emailData?.companyName ||
        "";

      // ── Address — gather raw fields then let the parser tidy them ────
      const rawAddress =
        emailData?.lead?.address ||
        emailData?.lead?.street ||
        emailData?.address ||
        emailData?.street ||
        emailData?.opportunity?.billingStreet ||
        emailData?.account?.billingStreet ||
        emailData?.appointmentData?.location ||
        "";
      const rawCity =
        emailData?.lead?.city ||
        emailData?.lead?.town ||
        emailData?.city ||
        emailData?.town ||
        emailData?.opportunity?.billingCity ||
        emailData?.account?.billingCity ||
        "";
      const rawPostcode =
        emailData?.lead?.postcode ||
        emailData?.lead?.zipCode ||
        emailData?.postcode ||
        emailData?.zipCode ||
        emailData?.opportunity?.billingPostalCode ||
        emailData?.account?.billingPostalCode ||
        "";

      const addressLines = parseAddressForPrint(
        rawAddress,
        rawCity,
        rawPostcode,
      );

      // ── Smart-parse body for letter sections ─────────────────────────
      const parsed = parseBodyForPrint(formData.body);
      const printSalutation =
        parsed.salutation ||
        `Dear ${recipientFirstName || recipientName.split(" ")[0]},`;

      // ── Print window ─────────────────────────────────────────────────
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Email Copy – ${emailPayload.subject}</title>
  <style>
    @page { margin: 1.5cm 2cm; }
    @media print {
      .no-print { display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; margin: 0 !important; padding: 0 !important; }
      body { margin: 0; }
      [contenteditable] { outline: none !important; border: none !important; background: transparent !important; box-shadow: none !important; cursor: text; }
      [contenteditable]:hover, [contenteditable]:focus { background: transparent !important; box-shadow: none !important; }
    }
    body {
      font-family: 'Calibri', 'Arial', sans-serif;
      color: #000;
      font-size: 11pt;
      line-height: 1.5;
      max-width: 21cm;
      margin: 0 auto;
      padding: 30px 40px;
      background: white;
    }
    .toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      padding: 12px 16px;
      background: #f1f5f9;
      border-radius: 8px;
      border: 1px solid #cbd5e1;
    }
    .print-btn {
      padding: 8px 20px;
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 6px;
      font-size: 14px;
      cursor: pointer;
      font-weight: 600;
    }
    .print-btn:hover { background: #1d4ed8; }
    .toolbar-hint {
      font-size: 13px;
      color: #64748b;
    }
    .toolbar-hint strong { color: #1e293b; }
    .letterhead {
      display: flex;
      align-items: flex-start;
      margin-bottom: 30px;
      border-bottom: 2px solid #e0e0e0;
      padding-bottom: 16px;
    }
    .letterhead-logo { flex: 0 0 auto; margin-right: 24px; }
    .letterhead-logo img { height: 80px; width: auto; display: block; }
    .letterhead-address {
      flex: 1;
      text-align: right;
      font-size: 9.5pt;
      line-height: 1.6;
      color: #333;
    }
    .letterhead-address .co-name {
      font-weight: bold;
      font-size: 10.5pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .letter-date { margin-bottom: 24px; font-size: 11pt; }
    .recipient-block { margin-bottom: 28px; line-height: 1.6; }
    .recipient-block p { margin: 0; padding: 0; }
    .recipient-block .recipient-name { font-weight: bold; }
    .salutation { margin-bottom: 18px; }
    .letter-body { white-space: pre-wrap; margin-bottom: 28px; }
    .closing { margin-bottom: 36px; }
    .signature-name {
      font-size: 20pt;
      font-weight: 900;
      letter-spacing: 2px;
      font-family: 'Arial Black', 'Arial', sans-serif;
      margin-bottom: 4px;
    }
    .signature-block p { margin: 0; padding: 0; font-weight: bold; }
    .signature-block .co-name { text-transform: uppercase; }
    [contenteditable="true"] {
      outline: none;
      border-radius: 3px;
      transition: background 0.15s, box-shadow 0.15s;
    }
    [contenteditable="true"]:hover { background: #f0f9ff; }
    [contenteditable="true"]:focus {
      background: #eff6ff;
      box-shadow: 0 0 0 2px #93c5fd;
      outline: none;
    }
  </style>
</head>
<body>
  <div class="no-print toolbar">
    <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <span class="toolbar-hint">✏️ <strong>Click any text</strong> to edit it before printing.</span>
  </div>

  <div class="letterhead">
    <div class="letterhead-logo">
      <img src="/assets/fibre-logo.png" alt="Fibre Telecoms UK" onerror="this.style.display='none'" />
    </div>
    <div class="letterhead-address">
      <div class="co-name">Fibre Telecoms (UK) Ltd</div>
      <div>Hercules House</div>
      <div>Merlin Quay</div>
      <div>Hazel Road</div>
      <div>Southampton</div>
      <div>SO19 7GB</div>
      <div>0330 094 8797</div>
    </div>
  </div>

  <div class="letter-date" contenteditable="true">${dateWithOrdinal}</div>

  <div class="recipient-block" contenteditable="true">
    <p class="recipient-name">${recipientName}</p>
    ${recipientCompany ? `<p>${recipientCompany}</p>` : ""}
    ${addressLines.map((line) => `<p>${line}</p>`).join("\n    ")}
  </div>

  <div class="salutation" contenteditable="true">${printSalutation}</div>

  <div class="letter-body" contenteditable="true">${parsed.body}</div>

  <div class="closing" contenteditable="true">${parsed.closing}</div>

  <div class="signature-name" contenteditable="true">${parsed.senderName}</div>
  <div class="signature-block">
    <p class="co-name" contenteditable="true">Fibre Telecoms UK</p>
  </div>
</body>
</html>`);
        printWindow.document.close();
      }

      onSuccess();
    } catch (error) {
      console.error("Error sending email:", error);
      alert(
        error.response?.data?.message ||
          "Failed to send email. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Send Email
            </h2>
            {emailStatus && (
              <div className="space-y-1 mt-1">
                <p
                  className={`text-sm ${
                    emailStatus.connected
                      ? isDarkMode
                        ? "text-green-400"
                        : "text-green-600"
                      : isDarkMode
                        ? "text-yellow-400"
                        : "text-yellow-600"
                  }`}
                >
                  {emailStatus.connected ? `✓ Connected` : emailStatus.message}
                </p>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-blue-400" : "text-blue-600"
                  }`}
                >
                  Sending from:{" "}
                  <span className="font-semibold">
                    diaryteam@fibretelecomsuk.co.uk
                  </span>{" "}
                  (Shared Inbox)
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <FileText size={16} className="inline mr-1" />
                Use Template
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              >
                <option value="">-- Select Template --</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {emailData?.appointmentData && (
                <p
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Available variables: fullName, companyName, appointmentTitle,
                  appointmentDate, appointmentTime, appointmentLocation,
                  assignedTo, senderEmail (diaryteam@fibretelecomsuk.co.uk)
                </p>
              )}
            </div>

            <div
              className={`border rounded-lg p-3 ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  From:
                </span>
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  "Fibre Telecoms UK" &lt;diaryteam@fibretelecomsuk.co.uk&gt;
                </span>
              </div>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Using shared inbox - replies will go to
                diaryteam@fibretelecomsuk.co.uk
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                To <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="to"
                value={formData.to}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  CC
                </label>
                <input
                  type="text"
                  name="cc"
                  value={formData.cc}
                  onChange={handleChange}
                  placeholder="email1@example.com, email2@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  BCC
                </label>
                <input
                  type="text"
                  name="bcc"
                  value={formData.bcc}
                  onChange={handleChange}
                  placeholder="email1@example.com, email2@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="body"
                value={formData.body}
                onChange={handleChange}
                required
                rows={12}
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
            </div>

            <div className="flex gap-4">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  name="trackOpens"
                  checked={formData.trackOpens}
                  onChange={handleChange}
                  className={`rounded focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                Track Opens
              </label>
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  name="trackLinks"
                  checked={formData.trackLinks}
                  onChange={handleChange}
                  className={`rounded focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                Track Link Clicks
              </label>
            </div>
          </div>

          <div
            className={`px-6 pt-4 pb-0 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            <p className="text-xs italic">
              Please be advised that meetings may occasionally be recorded for
              training purposes and to maintain full transparency between all
              parties.
            </p>
          </div>

          <div
            className={`p-6 border-t flex justify-between ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {emailData && (
                <p>
                  Sending to:{" "}
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {emailData.fullName}
                  </span>
                  {emailData.companyName && (
                    <>
                      {" "}
                      at{" "}
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {emailData.companyName}
                      </span>
                    </>
                  )}
                  {emailData.appointmentData && (
                    <span
                      className={`block text-xs mt-1 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Re: {emailData.appointmentData.title} –{" "}
                      {emailData.appointmentData.date}
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !emailStatus?.connected}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
              >
                <Send size={16} />
                {loading ? "Sending..." : "Send Email"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendEmailModal;
